/**
 * `50` — *{assign, assignBehavior} {someone, columnValue} as {assignee, columnId}*.
 * `add` appends to the existing `personsAndTeams` set and de-duplicates; anything else — the
 * field is absent on one of the two production rows — **replaces** (ASSUMED, open question 24,
 * because the UI labels only the non-default option "Add assignee").
 */
import { z } from 'zod';
import { defineAction } from '../block.js';
import { columnRefSchema, personIdListSchema, personsAndTeamsOf } from '../values.js';
export const assignPersonParams = z.object({
    columnId: columnRefSchema,
    /** P-278: monday's four shapes for this slot, ids out. */
    columnValue: personIdListSchema,
    assignBehavior: z
        .union([z.enum(['add', 'replace']), z.object({ title: z.string().optional(), value: z.enum(['add', 'replace']) })])
        .transform((raw) => (typeof raw === 'string' ? raw : raw.value))
        .optional(),
});
export const assignPersonAction = defineAction({
    id: '50',
    kind: 'action',
    app: 'monday',
    sentencePartial: '{assign, assignBehavior} {someone, columnValue} as {assignee, columnId}',
    paramsSchema: assignPersonParams,
    requiredColumnTypes: ['people', 'person'],
    executable: true,
    references: (params) => [
        { kind: 'column', id: params.columnId.columnId, invalid: params.columnId.invalid },
        ...params.columnValue.map((id) => ({ kind: 'user', id: String(id) })),
    ],
    async execute(context, params) {
        if (context.itemId === null)
            return { skipped: 'no_item' };
        const wanted = params.columnValue.map((id) => ({ id, kind: 'person' }));
        let personsAndTeams = wanted;
        if (params.assignBehavior === 'add') {
            const existing = personsAndTeamsOf(await context.services.getCellValue(context.itemId, params.columnId.columnId));
            const seen = new Set(existing.map((entry) => entry.id));
            personsAndTeams = [...existing, ...wanted.filter((entry) => !seen.has(entry.id))];
        }
        const result = await context.services.setCellValue({
            boardId: context.boardId,
            itemId: context.itemId,
            columnId: params.columnId.columnId,
            value: { personsAndTeams },
        });
        return { assigned: personsAndTeams.map((entry) => entry.id), changed: result.changed };
    },
});
//# sourceMappingURL=50.js.map